import api from '../utils/api';
import type { JanusRequestConfig } from '../utils/api';
import type {
  SwitchAccountResponse,
  SeatsSummary,
  SeatType,
  TenantFullResponse,
  AccountTenantSummary,
  SeatAddonPrice,
} from '../types/accounts';

/**
 * Normalises the BFF / billing seats-summary payload to the flat
 * snake_case shape declared by `SeatsSummary`. Two upstream variants
 * arrive in the wild today:
 *
 *  1. Billing-canonical (PR arch#17): nested per-type blocks
 *     `{sales: {used, total, extra, included}, executive: {...},
 *       seats_used_by_account, used_sales_seats, used_executive_seats,
 *       available_sales, available_executive, data_partial}`.
 *  2. Legacy flat: `{plan_sales_seats, total_sales_seats,
 *     extra_sales_seats, used_sales_seats, ...}`.
 *
 * The FE consumers (AdminBillingPage "Plano Atual" card, SeatQuotaBadge,
 * SeatsManagementPage, InvitationsPanel, useAccountStructure) all read
 * the flat snake_case names. Without this normalisation the nested
 * variant lands as `plan_sales_seats: undefined` everywhere → cards show
 * "0 Sales + 0 Executive ocupadas" and the badge shows "Sales: N/0"
 * even though the underlying data is correct (D3 frontend Bug 2).
 *
 * Falls through unchanged when the upstream already emitted the flat
 * keys, so this is a pure soft-coalesce — never overwrites a value that
 * was already supplied.
 */
/**
 * Preço da cadeira extra por TIPO — a forma real do fio
 * (`janus-billing seats_service._addon_price_brl` → `dict[str, float]`).
 *
 * `num()` NÃO serve aqui, e é literalmente o defeito que este helper fecha:
 * `Number({sales: 79, executive: 199})` é `NaN`, e o `num` devolve `0` para
 * `NaN` — a tela dizia R$ 0,00 num modal de cobrança recorrente.
 *
 * A ausência viaja como AUSÊNCIA (`undefined`), nunca como `0`: quando o
 * billing cai, o BFF serve 200 sem preço nenhum, e um zero ali seria a
 * plataforma afirmando gratuidade que ninguém mediu. Tipo por tipo, pelo
 * mesmo motivo — um preço presente não pode ser apagado por causa do outro.
 */
function precoDaCadeira(v: unknown): SeatAddonPrice | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const raw = v as Record<string, unknown>;
  const out: SeatAddonPrice = {};
  for (const tipo of ['sales', 'executive'] as const) {
    const n = typeof raw[tipo] === 'number' ? (raw[tipo] as number) : Number(raw[tipo]);
    if (Number.isFinite(n)) out[tipo] = n;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizeSeatsSummary(raw: unknown): SeatsSummary {
  const r = (raw ?? {}) as Record<string, unknown>;
  const sales = (r.sales ?? {}) as Record<string, unknown>;
  const exec = (r.executive ?? {}) as Record<string, unknown>;

  const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * Mesma disciplina do `pick` de {@link normalizeSeatsSummaryFromCamel}, e
   * pelo mesmo motivo: `0 ?? x` devolve `0`. Aqui a chave achatada costuma
   * chegar AUSENTE (e por isso o `??` acertava por sorte), mas o dia em que
   * este endpoint passar a mandar `0` explicito — como o agregador ja manda —
   * o Bug 2 reaparece nesta metade. Duas leituras da mesma pergunta divergem
   * na primeira vez que so' uma delas e' corrigida.
   */
  const pick = (...candidates: unknown[]): number => {
    for (const c of candidates) {
      const n = num(c);
      if (n !== 0) return n;
    }
    return 0;
  };

  const plan_sales_seats = pick(r.plan_sales_seats, sales.included);
  const plan_executive_seats = pick(r.plan_executive_seats, exec.included);
  const extra_sales_seats = pick(r.extra_sales_seats, sales.extra);
  const extra_executive_seats = pick(r.extra_executive_seats, exec.extra);
  const total_sales_seats = pick(
    r.total_sales_seats,
    sales.total,
    plan_sales_seats + extra_sales_seats,
  );
  const total_executive_seats = pick(
    r.total_executive_seats,
    exec.total,
    plan_executive_seats + extra_executive_seats,
  );
  const used_sales_seats = pick(r.used_sales_seats, sales.used);
  const used_executive_seats = pick(r.used_executive_seats, exec.used);

  return {
    ...(r as Record<string, unknown>),
    plan_slug: typeof r.plan_slug === 'string' ? r.plan_slug : '',
    plan_sales_seats,
    plan_executive_seats,
    extra_sales_seats,
    extra_executive_seats,
    total_sales_seats,
    total_executive_seats,
    used_sales_seats,
    used_executive_seats,
    max_tenant_admins: num(r.max_tenant_admins),
    addon_price_brl: precoDaCadeira(r.addon_price_brl),
    data_partial: r.data_partial === true,
  } as SeatsSummary;
}

/**
 * Mirror of {@link normalizeSeatsSummary} for the camelCase variant
 * the `/admin/tenants/:id/full` aggregator emits today (BFF
 * `projectFullPayload` lower-cases keys but doesn't flatten the nested
 * billing shape — so `seats.planSalesSeats` lands `0` even when billing
 * carries the data on `seats.sales.included`).
 */
function normalizeSeatsSummaryFromCamel(raw: unknown): SeatsSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const sales = (r.sales ?? {}) as Record<string, unknown>;
  const exec = (r.executive ?? {}) as Record<string, unknown>;

  const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * `??` NAO serve aqui, e essa foi a regressao do Bug 2.
   *
   * O docstring acima diz o fato que decide tudo: o agregador entrega
   * `seats.planSalesSeats` com o valor **`0`** — nao `null`, nao ausente —
   * enquanto o dado real viaja em `seats.sales.included`. E `0 ?? x` devolve
   * `0`: o fallback que esta funcao inteira existe para fazer NUNCA disparava,
   * e a tela mostrava "0 / 0 assentos" para uma conta com assentos pagos e em
   * uso. O admin conclui que nao tem cadeira e compra o que ja possui — ou
   * remove usuario que estava dentro da cota.
   *
   * `pick` trata o zero de um candidato como "nao informado" e segue para o
   * proximo. Um zero LEGITIMO continua saindo zero: quando nenhum candidato
   * tem valor, o retorno e' `0` — a diferenca e' que ele passa a ser o zero
   * de quem realmente nao tem assento, e nao o zero de quem tem e nao
   * conseguimos ler.
   *
   * Ordem dos candidatos = ordem de confianca: chave achatada camelCase,
   * depois o bloco aninhado do billing (a fonte), depois a snake_case legada.
   */
  const pick = (...candidates: unknown[]): number => {
    for (const c of candidates) {
      const n = num(c);
      if (n !== 0) return n;
    }
    return 0;
  };

  const plan_sales_seats = pick(r.planSalesSeats, sales.included, r.plan_sales_seats);
  const plan_executive_seats = pick(r.planExecutiveSeats, exec.included, r.plan_executive_seats);
  const extra_sales_seats = pick(r.extraSalesSeats, sales.extra, r.extra_sales_seats);
  const extra_executive_seats = pick(r.extraExecutiveSeats, exec.extra, r.extra_executive_seats);
  const total_sales_seats = pick(
    r.totalSalesSeats,
    sales.total,
    r.total_sales_seats,
    plan_sales_seats + extra_sales_seats,
  );
  const total_executive_seats = pick(
    r.totalExecutiveSeats,
    exec.total,
    r.total_executive_seats,
    plan_executive_seats + extra_executive_seats,
  );
  const used_sales_seats = pick(r.usedSalesSeats, sales.used, r.used_sales_seats);
  const used_executive_seats = pick(r.usedExecutiveSeats, exec.used, r.used_executive_seats);

  return {
    plan_slug: typeof r.planSlug === 'string' ? (r.planSlug as string) : '',
    plan_sales_seats,
    plan_executive_seats,
    extra_sales_seats,
    extra_executive_seats,
    total_sales_seats,
    total_executive_seats,
    used_sales_seats,
    used_executive_seats,
    max_tenant_admins: num(r.maxTenantAdmins ?? r.max_tenant_admins),
    addon_price_brl: precoDaCadeira(r.addonPriceBrl ?? r.addon_price_brl),
    data_partial: r.dataPartial === true || r.data_partial === true,
  };
}

/**
 * Account-layer API client (onda 3a).
 *
 * Wraps the BFF endpoints added in onda 2:
 *   - POST /auth/switch-account
 *   - GET  /admin/tenants/:tenantId/seats-summary
 *   - GET  /admin/tenants/:tenantId/full         (D3 W4b aggregator)
 *   - POST /admin/subscriptions/:tenantId/seats/add
 *   - DELETE /admin/subscriptions/:tenantId/seats/:seat_type
 */
export const accountsApi = {
  switchAccount: async (accountId: string): Promise<SwitchAccountResponse> => {
    const { data } = await api.post<SwitchAccountResponse>('/auth/switch-account', {
      account_id: accountId,
    });
    return data;
  },

  /**
   * Reissue a JWT scoped to a different tenant the user has membership in.
   * Used by the account_owner header tenant switcher — see Header.tsx.
   *
   * BFF route (POST /api/v1/auth/switch-tenant) validates membership in
   * `user_tenant_memberships` upstream and returns a camelCase payload.
   * Caller replaces `localStorage.accessToken`, clears the react-query
   * cache, and hard-reloads so every downstream API call re-scopes to
   * the new tenant.
   */
  switchTenant: async (
    tenantId: number,
  ): Promise<{
    accessToken: string;
    tokenType: string;
    expiresIn: number;
    currentTenantId: number;
  }> => {
    const { data } = await api.post<{
      accessToken: string;
      tokenType: string;
      expiresIn: number;
      currentTenantId: number;
    }>('/auth/switch-tenant', { tenant_id: tenantId });
    return data;
  },

  getSeatsSummary: async (tenantId: string): Promise<SeatsSummary> => {
    const { data } = await api.get<unknown>(`/admin/tenants/${tenantId}/seats-summary`, {
      __silentStatuses: [403],
    } as JanusRequestConfig);
    // BFF emits the billing-canonical nested shape (`{sales: {used, total,
    // extra, included}, executive: {...}}`) plus a few flat helpers — but
    // *not* `plan_sales_seats` / `total_sales_seats`. Without normalisation
    // every consumer that reads the flat keys lands on `undefined` → 0,
    // which is exactly the "0 Sales + 0 Executive ocupadas" symptom on
    // /admin/billing (D3 frontend Bug 2).
    return normalizeSeatsSummary(data);
  },

  /**
   * D3 W4b composite endpoint. Returns `{tenant, subscription, seats,
   * dataPartial}` in one round-trip. Replaces the legacy three-fetch
   * pattern on AdminBillingPage. The BFF still surfaces 200 + a
   * `dataPartial: true` flag when one upstream times out — callers
   * should reflect that in the UI rather than treating it as success.
   *
   * The `seats` block is camelCase per the BFF aggregator
   * (`projectFullPayload`), but billing keeps its nested per-type
   * shape, so a few keys can still arrive as 0 (`planSalesSeats`,
   * `usedSalesSeats`). We re-normalise to the flat snake_case
   * `SeatsSummary` shape that AdminBillingPage / SeatQuotaBadge /
   * SeatsManagementPage all read — same fix as `getSeatsSummary`.
   */
  getTenantFull: async (tenantId: string | number): Promise<TenantFullResponse> => {
    const { data } = await api.get<TenantFullResponse>(`/admin/tenants/${tenantId}/full`);
    if (data && typeof data === 'object' && data.seats) {
      const normalised = normalizeSeatsSummaryFromCamel(data.seats as unknown);
      if (normalised) {
        return { ...data, seats: normalised } as TenantFullResponse;
      }
    }
    return data;
  },

  addSeat: async (tenantId: string, seatType: SeatType): Promise<void> => {
    await api.post(`/admin/subscriptions/${tenantId}/seats/add`, {
      seat_type: seatType,
    });
  },

  removeSeat: async (tenantId: string, seatType: SeatType): Promise<void> => {
    await api.delete(`/admin/subscriptions/${tenantId}/seats/${seatType}`);
  },

  /**
   * D3 W19 — list tenants under a given account. Used by the multi-tenant
   * invite form so an account-scoped admin can pick one or more tenants
   * to grant the invitee access to. The BFF endpoint is cache-tagged for
   * ~60s so callers should mirror the staleTime to avoid refetch storms.
   */
  getAccountTenants: async (accountId: string): Promise<AccountTenantSummary[]> => {
    const { data } = await api.get<AccountTenantSummary[] | { tenants: AccountTenantSummary[] }>(
      `/accounts/${accountId}/tenants`,
    );
    return Array.isArray(data) ? data : (data?.tenants ?? []);
  },
};
